import {
  getCurrentUser,
  getCurrentUserId,
  getPreference,
} from 'conversations/modules/user/selectors'
import { buildMyConversationsQuery } from 'conversations/utils/ConversationSearchUtil'
import { captureError } from 'conversations/utils/logRocket'
import lodashMerge from 'lodash/merge'
import { Epic } from 'redux-observable'
import { empty, from, of } from 'rxjs'
import { catchError, debounceTime, filter, map, pluck, switchMap } from 'rxjs/operators'
import { showToastNotification } from 'shared/store/actions/toastNotification'
import {
  closeAllMyConversationsAsync,
  setDraftPreference,
  setPreference,
  setPreferenceOff,
  setPreferenceOn,
  togglePreference,
  trafficControlUpdateUserConfig,
  updateUserPreferenceAsync,
} from 'shared/store/actions/user'

import { GlobalStore as store, receiveUserConfig } from '@driftt/traffic-control'

import { isActionOf, RootAction, RootState, Services } from 'typesafe-actions'

import * as api from './api'

export * from './sections/epic'

export const handleTogglePreference: Epic<RootAction, RootAction, RootState, Services> = (
  action$,
  state
) =>
  action$.pipe(
    filter(isActionOf([togglePreference, setPreferenceOn, setPreferenceOff])),
    pluck('payload'),
    switchMap((payload) => {
      const newValue = getPreference(payload)(state.value)
      from(api.updateUserPreference(payload, newValue)).pipe(
        catchError((error) => {
          captureError(error, 'Toggle user preference failed')
          return empty()
        })
      )
      return empty()
    })
  )

export const updateUserPreference: Epic<RootAction, RootAction, RootState, Services> = (
  action$,
  state
) =>
  action$.pipe(
    filter(isActionOf(updateUserPreferenceAsync.request)),
    switchMap((action) => {
      const oldValue = getPreference(action.payload.preference)(state.value)
      store.dispatch(setDraftPreference(action.payload))
      return from(api.updateUserPreference(action.payload.preference, action.payload.value)).pipe(
        map(() => {
          return updateUserPreferenceAsync.success(action.payload)
        }),
        catchError((error: Error) => {
          store.dispatch(
            showToastNotification({
              body: 'Unable to update composer preferences, please try again.',
            })
          )
          captureError(error, 'Update user preference failed')
          store.dispatch(
            updateUserPreferenceAsync.failure({
              preference: action.payload.preference,
              value: oldValue,
            })
          )
          return empty()
        })
      )
    })
  )

export const handleSetPreference: Epic<RootAction, RootAction, RootState, Services> = (
  action$,
  state
) =>
  action$.pipe(
    filter(isActionOf(setPreference)),
    pluck('payload'),
    switchMap((payload) => {
      const newValue = getPreference(payload.preference)(state.value)
      from(api.updateUserPreference(payload.preference, newValue)).pipe(
        catchError((error) => {
          captureError(error, 'Set user preference failed')
          return empty()
        })
      )
      return empty()
    })
  )

export const closeAllMyConversations: Epic<RootAction, RootAction, RootState, Services> = (
  action$,
  state
) =>
  action$.pipe(
    filter(isActionOf(closeAllMyConversationsAsync.request)),
    debounceTime(1000),
    switchMap(() => {
      return from(
        api.hideAllConversations({
          ...buildMyConversationsQuery({
            userId: getCurrentUserId(state.value) ?? 0,
          }),
          // Filter groups requires an array of arrays of user ids, but hideAll does not so override that here
          participantIds: [getCurrentUserId(state.value) ?? 0],
          cursor: undefined,
        })
      ).pipe(
        map(closeAllMyConversationsAsync.success),
        catchError((error: Error) => {
          captureError(error, 'Close all conversations failed', {
            userId: getCurrentUserId(state.value),
          })
          return of(closeAllMyConversationsAsync.failure(error))
        })
      )
    })
  )

export const handleUserDispatchToTC: Epic<RootAction, RootAction, RootState, Services> = (
  action$
) =>
  action$.pipe(
    filter(isActionOf(trafficControlUpdateUserConfig)),
    pluck('payload'),
    switchMap((payload) => {
      const currentUser = getCurrentUser(store.getState())
      const { workspaceRoles = [], features = {} } = currentUser || {}
      store.dispatch(receiveUserConfig(lodashMerge({}, { workspaceRoles, features }, payload)))
      return empty()
    })
  )
